import {useEffect, useRef} from 'react';

import clsx from 'clsx';
import {Editor, OnMount} from '@monaco-editor/react';
import {initVimMode, VimMode} from 'monaco-vim';
import * as monaco from 'monaco-editor';

import {DEFAULT_EDITOR_SETTINGS, EditorUserSettings} from './EditorSettingsModal';

type CodeEditorProps = {
  title?: string;
  className?: string;
  code: string;
  onChange?: (newValue: string | undefined) => void;
  height?: number;
  readOnly?: boolean;
  language?: string;
  isMainEditor?: boolean;
  settings?: Partial<EditorUserSettings>;
};

export const CodeEditor = ({
  title,
  className,
  code,
  onChange,
  height,
  readOnly = false,
  language = 'java',
  settings,
}: CodeEditorProps) => {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const vimModeRef = useRef<VimMode | null>(null);

  const handleEditorMount: OnMount = (editor: monaco.editor.IStandaloneCodeEditor, _monacoInstance: typeof monaco) => {
    editorRef.current = editor;
    if (editorRef.current && statusRef.current) {
      vimModeRef.current = initVimMode(editorRef.current, statusRef.current);
    }
  };

  useEffect(() => {
    const editor = editorRef.current;
    const statusBar = statusRef.current;
    const enabled = (settings && settings.enableVimMode) ?? DEFAULT_EDITOR_SETTINGS.enableVimMode;

    if (vimModeRef.current) {
      vimModeRef.current.dispose();
      vimModeRef.current = null;
    }

    if (enabled && editor && statusBar) {
      vimModeRef.current = initVimMode(editor, statusBar);
    }

    return () => {
      if (vimModeRef.current) {
        vimModeRef.current.dispose();
        vimModeRef.current = null;
      }
    };
  }, [settings]);

  return (
    <>
      {title && (
        <div className="bg-[#161616]">
          <div className="ml-7 py-2">{title}</div>
        </div>
      )}
      <Editor
        className={clsx(className)}
        language={language}
        theme="vs-dark"
        onChange={(value, _) => {
          onChange && onChange(value);
        }}
        value={code}
        height={height}
        options={{
          readOnly,
          automaticLayout: true,
          scrollBeyondLastLine: false,
          scrollbar: {
            alwaysConsumeMouseWheel: false,
          },
          detectIndentation: false,

          minimap: {
            enabled: (settings && settings.enableMinimap) ?? DEFAULT_EDITOR_SETTINGS.enableMinimap,
          },
          lineNumbers: (settings && settings.lineNumbers) ?? DEFAULT_EDITOR_SETTINGS.lineNumbers,
          fontSize: settings?.fontSize ?? DEFAULT_EDITOR_SETTINGS.fontSize,
          tabSize: settings?.tabSize ?? DEFAULT_EDITOR_SETTINGS.tabSize,
          wordWrap: settings?.wordWrap ?? DEFAULT_EDITOR_SETTINGS.wordWrap,
        }}
        onMount={handleEditorMount}
      />
      <div
        id="my-statusbar"
        className="w-full flex justify-between items-center px-2 bg-gray-800 text-gray-100 select-none"
      >
        {settings?.enableVimMode && <div className="h-6 bg-gray-800 text-gray-100 px-2  w-full" ref={statusRef} />}
      </div>
    </>
  );
};
